<template>

    <div>
        <div class="background" :class="backgroundClass()"></div>
        <div v-on:click="close" class="close">
            <img src="../assets/close-white-18dp.svg">
        </div>
        <!-- Heading -->
        <div class="wrapper">
            <div class="heading">
                Post Recipe
            </div>
        </div>
        <!-- Step 1 - Recipe Title -->
        <div v-if="step == 1" class="wrapper">
            <div class="intro">
                Choose a title for your recipe<br>
                Something simple and descriptive<br>
                "Spaghetti and Meatballs"
            </div>
            <div class="title text-center">
                <form v-on:submit.prevent>
                    <div class="form-group">
                        <div class="input-group mb-3">
                            <label :class="labelClass" class="label" for="title">recipe title</label>
                            <label :class="countClass(title.length, 32)" class="count" for="title">{{title.length}} / 32</label>
                            <input
                                :value="title"
                                @input="title = $event.target.value"
                                autocapitalize="words"
                                autocomplete="off"
                                autocorrect="on"
                                spellcheck="true"
                                type="text"
                                ref='title'
                                :class="inputClass"
                                class="form-control"
                                id="title" 
                                placeholder=""
                                maxlength="32"
                            >
                        </div>
                    </div>
                </form>
                <transition name="fade" appear>
                    <div class="errors" v-if="errors.length > 0">
                        <div :class="[ valid ? 'valid' : 'error']">
                            {{ errors[0] }}
                        </div>
                    </div>
                </transition>
            </div>
            <div v-on:click="next" class="next" v-bind:class="[ valid ? 'valid' : 'invalid']">
                Next
            </div>
        </div>
        <!-- Step 2 - Upload Image -->
        <div v-if="step == 2" class="wrapper">
            <div class="input-group mb-3">
                <div class="custom-file">
                    <label class="custom-file-label" for="image" >{{ fileName }}</label>
                    <input class="custom-file-input" type="file" id="file" ref="file" v-on:change="handleFileUpload()">
                    
                </div>
            </div>
            <div class="image" v-if="preview">
                <img v-bind:src="preview">
            </div>
            <div v-if="loadingImage" class="loading">
                <transition name="fade-delay" appear>
                    <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
                </transition>
            </div>
            <div v-on:click="next" class="next" v-bind:class="[ preview ? 'valid' : 'invalid']">
                Next
            </div>
        </div>
        <!-- Step 3 - Add details -->
        <div v-if="step == 3" class="wrapper">
            <div class="step-3-form">
                <div>
                    <form v-on:submit.prevent>
                        <div class="form-group">
                            <!-- Description -->
                            <div class="detail">
                                <label class="details-label" for="description">Short recipe description</label>
                                <label :class="countClass(description.length, 64)" class="details-count" for="description">{{description.length}} / 64</label>
                                <textarea
                                    @keydown.enter.exact.prevent
                                    @input="description = $event.target.value"
                                    :value="description"
                                    id="description"
                                    class="textarea description"
                                    maxlength="64"
                                    placeholder="Traditional spaghetti and meatballs"
                                ></textarea>
                            </div>
                            <!-- Story -->
                            <div class="detail">
                                <label class="details-label" for="story">Story behind the recipe</label>
                                <label :class="countClass(story.length, 1024)" class="details-count" for="story">{{story.length}} / 1024</label>
                                <textarea
                                    @input="story = $event.target.value"
                                    :value="story"
                                    id="story"
                                    class="textarea story"
                                    maxlength="1024"
                                    placeholder="My Nonna's famous spaghetti and meatballs. Tastes like a big warm hug from a little old Italian woman. A weekly staple in my house!"
                                ></textarea>
                            </div>
                            <!-- Component Names -->
                            <div class="detail">
                                <label class="details-label" for="componentNames">Components</label>
                                <div v-for="(component,index) in componentNames" v-bind:key="index">
                                    <textarea
                                        @keydown.enter.exact.prevent
                                        v-on:keydown.enter="listFocus($event,0)"
                                        @input="updateComponentNames(index, $event.target.value.charAt(0).toUpperCase() + $event.target.value.slice(1))"
                                        class="textarea textarea-list component-names"
                                        maxlength="32"
                                        :id="`component-names-${index}`"
                                        :placeholder="componentNamesPlaceholder[index]"
                                    >
                                    </textarea>
                                </div>
                            </div>
                            <!-- Ingredients -->
                            <div v-if="componentNames[0]" class="detail">
                                <label class="details-label" for="ingredients">Ingredients</label>
                                <div class="ingredients" v-for="(component,componentIndex) in components" v-bind:key="componentIndex">
                                    <div class="ingredient-heading">
                                        {{ component['name'] }}
                                    </div>
                                    <div v-for="(ingredient,IngredientIndex) in component['ingredients']" v-bind:key="componentIndex-IngredientIndex">
                                        <textarea
                                            @keydown.enter.exact.prevent
                                            v-on:keydown.enter="listFocus($event,0)"
                                            @input="updateComponents(componentIndex, IngredientIndex, $event.target.value)"
                                            class="textarea textarea-list"
                                            :id="`components-${componentIndex}-${IngredientIndex}`"
                                        >
                                        </textarea>
                                    </div>
                                </div>
                            </div>
                            <!-- Method -->
                            <div class="detail">
                                <label class="details-label" for="method">Method</label>
                                <div v-for="(instruction,index) in method" v-bind:key="index">
                                    <div class="method-count">{{ index + 1 }}</div>
                                    <textarea
                                        @keydown.enter.exact.prevent
                                        v-on:keydown.enter="listFocus($event,1)"
                                        @input="updateMethod(index, $event.target.value)"
                                        class="textarea textarea-list method"
                                        :id="`method-${index}`"
                                    >
                                    </textarea>
                                </div>
                            </div>
                            <!-- Tags -->
                            <div class="detail">
                                <label class="details-label" for="tags">Tags</label>
                                <label :class="countClass(tags.length, 32)" class="details-count" for="tags">{{tags.length}} / 32</label>
                                <textarea
                                    @keydown.enter.exact.prevent
                                    @input="tags = $event.target.value.toLowerCase()"
                                    :value="tags"
                                    class="textarea tags"
                                    maxlength="32"
                                    placeholder="meatballs pasta italian"
                                ></textarea>
                            </div>
                            <!-- Serves -->
                            <div class="detail">
                                <label class="details-label" for="serves">Serves</label>
                                <div class="btn-group btn-group-toggle" data-toggle="buttons">
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 1) }">
                                        <input v-model="servings" value="1" type="radio" name="options" id="1" autocomplete="off"> 1
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 2) }">
                                        <input v-model="servings" value="2" type="radio" name="options" id="2" autocomplete="off"> 2
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 3) }">
                                        <input v-model="servings" v-bind:value="3" type="radio" name="options" id="option3" autocomplete="off"> 3
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 4) }">
                                        <input v-model="servings" v-bind:value="4" type="radio" name="options" id="option4" autocomplete="off"> 4
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 5) }">
                                        <input v-model="servings" v-bind:value="5" type="radio" name="options" id="option5" autocomplete="off"> 5
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 6) }">
                                        <input v-model="servings" v-bind:value="6" type="radio" name="options" id="option6" autocomplete="off"> 6
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 7) }">
                                        <input v-model="servings" v-bind:value="7" type="radio" name="options" id="option7" autocomplete="off"> 7
                                    </label>
                                    <label class="btn btn-outline-secondary" :class="{ active: (servings == 8) }">
                                        <input v-model="servings" v-bind:value="8" type="radio" name="options" id="option8" autocomplete="off"> 8
                                    </label>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                <div class="wrapper end">
                    <div v-on:click="next" class="next done" v-bind:class="[ done ? 'valid' : 'invalid']">
                        Next
                    </div>
                </div>
            </div>
        </div>
        <!-- Step 4 - Done! -->
        <div v-if="step == 4" class="wrapper">
            <div class="tick"></div>
            <div class="done-message">
                Your recipe has been submitted for review by our chief editor<br><br>
            </div>
            <div v-on:click="next" class="next valid complete">
                Done
            </div>
        </div>
        <!-- Progress -->
        <div class="wrapper">
            <div v-if="step < 3" class="steps">
                <div class="step">
                    <svg height="20" width="20">
                        <circle cx="10" cy="10" r="10" :class="circleClass(1)" />
                    </svg>
                </div>
                <div class="step">
                    <svg class="circle" height="20" width="20">
                        <circle cx="10" cy="10" r="10" :class="circleClass(2)" />
                    </svg>
                </div>
                <div class="step">
                    <svg class="circle" height="20" width="20">
                        <circle cx="10" cy="10" r="10" :class="circleClass(3)" />
                    </svg>
                </div>
            </div>
        </div>
    </div>
    
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
export default {
    name: 'Post',
    data () {
        return {
            step: 1,
            title: '',
            description: '',
            story: '',
            componentNames: [''],
            componentNamesPlaceholder: ['Meatballs','Pasta'],
            components: [
                {
                    name: '',
                    ingredients: ['']
                }
            ],
            method: [''],
            tags: '',
            servings: false,
            id: '',
            url: '',
            valid: false,
            thinking: true,
            available: false,
            errors: [],
            file: false,
            preview: false,
            loadingImage: false
        }
    },
    created: function () {
        if (!this.authenticated) {
            this.login()
        }
    },
    methods: {
        close: function () {
            this.$router.push('/')
        },
        login: function () {
            window.location.replace("https://" + window.authEndpoint + "/login?response_type=code&client_id=" + window.cognitoAppClientId + "&redirect_uri=https://" + window.redirectUri)
        },
        log: function (e) {
            console.log(e)
        },
        listFocus: function (e,child){
            if(e.target.parentElement.nextElementSibling){
                e.target.parentElement.nextElementSibling.children[child].focus()
            }
        },
        updateMethod: function (index, value) {
            if (value || index < (this.method.length - 2) ) {
                this.method.splice(index, 1, value)
            } else {
                this.method.splice(index, 1)
            }
        },
        updateComponentNames: function (index, value) {
            if (value || index < (this.componentNames.length - 2) ) {
                this.componentNames.splice(index, 1, value)
            } else {
                this.componentNames.splice(index, 1)
                this.components.splice(index, 1)
            }
        },
        updateComponents: function (componentIndex, IngredientIndex, value) {
            if (value || IngredientIndex < (this.components[componentIndex].ingredients.length - 2) ) {
                this.components[componentIndex].ingredients.splice(IngredientIndex, 1, value)
                this.components = [].concat(this.components)
            } else {
                this.components[componentIndex].ingredients.splice(IngredientIndex, 1)
                this.components = [].concat(this.components)
            }
        },
        backgroundClass: function () {
            return 'background-step-' + this.step
        },
        countClass: function (count, max) {
            if (count >= max) {
                return 'count-invalid'
            } else {
                return 'count-standard'
            }
        },
        next: function () {
            // Step 1
            if (this.step == 1) {
                if (this.valid) {
                    let vm = this
                    vm.withAuth(function () {
                        var headers = {}
                        headers['Authorization'] = 'Bearer ' + vm.$store.getters.id_token
                        axios.post('/api/author/recipes', { title: vm.title }, { headers: headers })
                            .then(response => {
                                if (response.data) {
                                    console.log(response.data)
                                    vm.id = response.data.id
                                    vm.step++
                                }
                            })
                            .catch(error => {
                                console.log(error)
                            })
                    })
                }
            // Step 2
            } else if (this.step == 2) {
                if (this.preview) {
                    this.step++
                }
            // Step 3
            } else if (this.step == 3) {
                if ( this.done ) {
                    let vm = this
                    vm.withAuth(function () {
                        var headers = {}
                        var body = {
                            description: vm.description,
                            story: vm.story,
                            components: vm.components.map(c => {
                                return {
                                    name: c.name,
                                    ingredients: c.ingredients.slice(0, -1)
                                }
                            }),
                            method: vm.method.slice(0, -1),
                            tags: vm.tags,
                            servings: vm.servings
                        }
                        headers['Authorization'] = 'Bearer ' + vm.$store.getters.id_token
                        axios.put('/api/author/recipes/' + vm.id, body, { headers: headers })
                            .then(response => {
                                if (response.data) {
                                    console.log(response.data)
                                    vm.step++
                                }
                            })
                            .catch(error => {
                                console.log(error)
                            })
                    })
                }
            // Step 4
            } else if (this.step == 4) {
                this.$router.replace('/')
            }
        },
        circleClass: function(circle) {
            if (circle == this.step) {
                return 'circle-active'
            } else {
                return 'circle-inactive'
            }
        },
        handleFileUpload: function() {
            this.file = this.$refs.file.files[0];
            this.loadingImage = true
            let vm = this
            let formData = new FormData();
            formData.append('file', vm.file);
            vm.withAuth(function () {
                var headers = {}
                headers['Authorization'] = 'Bearer ' + vm.$store.getters.id_token
                headers['Content-Type'] = 'multipart/form-data'
                axios.post('/api/images/' + vm.id, formData, { headers: headers })
                    .then(response => {
                        if (response.data) {
                            console.log(response.data)
                            vm.preview = response.data.preview
                            vm.loadingImage = false
                        }
                    })
                    .catch(error => {
                        console.log(error)
                    })
            })
        }
    },
    computed: {
        ...mapGetters([
            'authenticated'
        ]),
        fileName() {
            if (this.file) {
                return this.file.name
            } else {
                return 'Choose Recipe Image'
            }
        },
        inputClass() {
            if (this.thinking) {
                return 'input-unknown'
            }
            if (this.valid) {
                return 'input-valid'
            } else {
                return 'input-invalid'
            }
        },
        labelClass() {
            if (!this.thinking && !this.valid) {
                return 'label-invalid'
            } else {
                return 'label-standard'
            }
        },
        done() {
            return (
                this.description && 
                this.story && 
                this.method.length > 1 && 
                this.components[0].name &&
                this.components[0].ingredients[0] && 
                this.servings
            )
        }
    },
    watch: {
        step: function () {
            if (this.step == 3) {
                this.$nextTick(() => {
                    // Set story input height
                    const storyEl = document.getElementById('story')
                    storyEl.style.height = '0px'
                    storyEl.style.height = storyEl.scrollHeight + 10 + 'px'
                    // Set description input height
                    const descriptionEl = document.getElementById('description')
                    descriptionEl.style.height = '0px'
                    descriptionEl.style.height = descriptionEl.scrollHeight + 'px'
                })
            }
        },
        title: function () {
            let vm = this
            vm.url = false
            vm.errors = []
            vm.id = ''
            vm.valid = false
            vm.thinking = true
            if (vm.title.length != 0) {
                vm.withAuth(function () {
                    let titleEncoded = encodeURIComponent(vm.title)
                    var headers = {}
                    headers['Authorization'] = 'Bearer ' + vm.$store.getters.id_token
                    axios.get('/api/check/' + titleEncoded, { headers: headers })
                        .then(response => {
                            if (response.data) {
                                console.log(response.data)
                                if (vm.title == response.data.requested) {
                                    vm.id = response.data.id
                                    vm.url = response.data.url
                                    vm.valid = response.data.valid
                                    vm.available = response.data.available
                                    vm.errors = response.data.errors
                                    vm.thinking = false
                                }
                            }
                        })
                        .catch(error => {
                            console.log(error)
                        })
                })
            }
        },
        description: function () {
            const el = document.getElementById('description')
            // Remove line breaks from input
            if (el.value.match(/\n/g)) {
                el.value = el.value.replace(/(\r\n|\n|\r)/gm, "")
                this.description = el.value
            }
            // Auto resize input fields
            el.style.height = 0
            el.style.height = el.scrollHeight + 'px'
        },
        story: function () {
            const el = document.getElementById('story')
            // Auto resize input fields
            el.style.height = 0
            el.style.height = el.scrollHeight + 'px'
        },
        componentNames: function () {
            this.$nextTick(() => {
                this.componentNames.forEach((component,index) => {
                    const el = document.getElementById(`component-names-${index}`)
                    // Remove line breaks from input, and focus next field
                    if (el.value.match(/\n/g)) {
                        el.value = el.value.replace(/(\r\n|\n|\r)/gm, "")
                        this.method.splice(index, 1, el.value)
                        el.parentElement.nextElementSibling.children[0].focus()
                    }
                    // Auto resize input fields
                    el.style.height = 0
                    el.style.height = el.scrollHeight + 'px'
                })
            })
            // Add new input field if the last one has content
            if (this.componentNames[this.componentNames.length - 1]) {
                this.componentNames.push('')
            }
            // Add place holders as required
            if (this.componentNames.length > this.componentNamesPlaceholder.length) {
                this.componentNamesPlaceholder.push('')
            }
            // Add component for each component name
            this.componentNames.forEach((componentName,index) => {
                if (componentName) {
                    if (!this.components[index]) {
                        this.components[index] = {
                            name: componentName,
                            ingredients: ['']
                        }
                        this.nextComponentId++
                    } else {
                        this.components[index].name = componentName
                    }
                } else {
                    if (this.components[index]) {
                        this.components[index].name = componentName
                    }
                }
            })
        },
        method: function () {
            this.$nextTick(() => {
                this.method.forEach((instruction,index) => {
                    const el = document.getElementById(`method-${index}`)
                    // Remove line breaks from input, and focus next field
                    if (el.value.match(/\n/g)) {
                        el.value = el.value.replace(/(\r\n|\n|\r)/gm, "")
                        this.method.splice(index, 1, el.value)
                        el.parentElement.nextElementSibling.children[1].focus()
                    }
                    // Auto resize input fields
                    el.style.height = 0
                    el.style.height = el.scrollHeight + 'px'
                })
            })
            // Add new input field if the last one has content
            if (this.method[this.method.length - 1]) {
                this.method.push('')
            }
        },
        components: function () {
            this.components.forEach((component,componentIndex) => {
                this.$nextTick(() => {
                    this.components[componentIndex].ingredients.forEach((ingredient,IngredientIndex) => {
                        const el = document.getElementById(`components-${componentIndex}-${IngredientIndex}`)
                        // Remove line breaks from input, and focus next field
                        if (el.value.match(/\n/g)) {
                            el.value = el.value.replace(/(\r\n|\n|\r)/gm, "")
                            this.components[componentIndex].ingredients.splice(IngredientIndex, 1, el.value)
                            el.parentElement.nextElementSibling.children[0].focus()
                        }
                        // Auto resize input fields
                        el.style.height = 0
                        el.style.height = el.scrollHeight + 'px'
                    })
                })
                // Add new input field if the last one has content
                if (this.components[componentIndex].ingredients[this.components[componentIndex].ingredients.length - 1]) {
                    this.components[componentIndex].ingredients.push('')
                }
            })
        },
        tags: function () {
            if (this.tags.match(/\n/g)) {
                this.tags = this.tags.replace(/(\r\n|\n|\r)/gm, "")
            }
        }
    }
}
</script>

<style scoped>
    .background {
        position: absolute;
        height: 100%;
        width: 100%;
        background-size: 100% 100%;
        background-repeat: no-repeat;
    }
    .background-step-1 {
        background-image: url(../assets/bbg1.svg);
    }
    .background-step-2 {
        background-image: url(../assets/bbg2.svg);
    }
    .background-step-3 {
        background-image: url(../assets/bbg3.svg);
    }
    .background-step-4 {
        background-image: url(../assets/bbg4.svg);
    }
    .close {
        position: absolute;
        top: 0px;
        right: 0px;
        padding: 22px;
    }
    .close img {
        width: 30px;
        height: 30px;
    }
    .done-message {
        text-align: center;
        padding: 30px;
        font-size: 1.2em;
        font-weight: 600;
        font-family: 'Comfortaa', Arial;
        color: #ffffff;
        position: absolute;
        top: 15vh;
        width: 100%;
    }
    .tick {
        position: absolute;
        top: 0px;
        width: 100vw;
        max-width: 50vh;
        height: 100%;
        background-image: url(../assets/check_circle_outline-white-18dp.svg);
        background-size: 50%;
        background-position: 50% 50%;
        background-repeat: no-repeat;
    }
    .steps {
        bottom: 2vh;;
        padding: 20px;
        color: #5B7083;
        display: flex;
        position: absolute;
    }
    .step {
        margin-left: 30px;
        margin-right: 30px;
    }
    .circle-active {
        fill: #00B4CC;
    }
    .circle-inactive {
        fill: #CED4DA;
    }
    .wrapper {
        justify-content: center;
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .heading {
        margin: 20px;
        font-size: 1.4em;
        font-weight: 600;
        font-family: 'Comfortaa', Arial;
        color: #ffffff;
        z-index: 1000;
    }
    @media (min-height: 550px) {
        .intro {
            font-size: 1.1em;
            font-weight: 100;
            font-family: 'Comfortaa', Arial;
            line-height: 5vh;
            position: absolute;
            top: 42vh;
            text-justify: auto;
            text-align: center;
            /* text-align-last: justify; */
            max-width: 85vw;
            width: 600px;
            padding-left: 2px;
        }
    }
    @media (max-height: 550px) {
        .intro {
            display: none;
        }
    }
    .title {
        top: 25vh;
        position: absolute;
    }
    .label {
        position: absolute;
        padding-left: 10px;
        padding-top: 6px;
        z-index: 1000;
        font-size: 1em;
        font-family: 'Comfortaa', Arial;
        color: #5B7083;
    }
    .count {
        position: absolute;
        right: 0px;
        padding-right: 10px;
        padding-top: 6px;
        z-index: 1000;
        font-size: 1em;
        font-family: 'Comfortaa', Arial;
        color: #5B7083;
    }
    .details-label {
        position: relative;
        left: 0px;
        padding-top: 6px;
        font-size: 1em;
        font-family: 'Comfortaa', Arial;
        color: #5B7083;
    }
    .details-count {
        position: absolute;
        right: 0px;
        padding-top: 6px;
        font-size: 1em;
        font-family: 'Comfortaa', Arial;
        color: #5B7083;
    }
    .errors {
        position: relative;
    }
    .url {
        bottom: 30vh;
        position: absolute;
        font-size: 1.2em;
        font-family: 'Comfortaa', Arial;
        color: #00B4CC;
    }
    .next {
        bottom: 20vh;
        position: absolute;
        color: #ffffff;
        font-size: 1.6em;
        font-weight: 900;
        letter-spacing: 0.2em;
        font-family: 'Comfortaa', Arial;
        padding: 10px 100px;
        max-width: 85vw;
    }
    .next:hover {
        cursor: pointer;
    }
    .done {
        /* position: absolute; */
        bottom: unset;
    }
    .complete {
        bottom: 10vh;
    }
    .end {
        height: 120px;
    }
    .custom-file {
        margin: 10px;
        overflow: hidden;
    }
    .image {
        top: 22vh;
        position: absolute;
    }
    .image img {
        max-width: 100vw;
        max-height: 45vh;
    }
    .loading-image {
        top: 50vh;
        position: absolute;
    }
    .loading-image img {
        top: -20px;
    }
    .input-group {
        width: 600px;
        max-width: 85vw;
        margin-bottom: 0px !important;
    }
    input:focus, textarea:focus, select:focus {
        outline: none;
    }
    .input-valid, .input-valid:focus {
        border-color: #00B4CC;
    }
    .input-unknown, .input-unknown:focus {
        border-color: #CED4DA;
    }
    .input-invalid, .input-invalid:focus {
        border-color: #E0245E;
    }
    .label-standard {
        color: #5B7083;
    }
    .label-invalid {
        color: #E0245E;
    }
    .count-standard {
        color: #5B7083;
    }
    .count-invalid {
        color: #E0245E;
    }
    .valid {
        background-color: #00B4CC;  
    }
    .invalid {
        background-color: #CED4DA;
        color: #5B7083;
    }
    @media (max-width: 550px) {
        .error {
            color: #E0245E;
            font-size: 0.7em;
        }
    }
    @media (min-width: 550px) {
        .error {
            color: #E0245E;
            font-size: 1.2em;
        }
    }
    .step-3-form {
        position: absolute;
        top: 17vh;
    }
    .form-group {
        margin-bottom: 10px;
    }
    .form-control {
        backdrop-filter: blur(16px);
        color: #000000;
        font-family: 'Comfortaa', Arial;
        font-size: 1.6em;
        text-align: center;
        padding-top: 60px;
        padding-bottom: 30px;
        outline: none;
        box-shadow: none;
        border-radius: 0;
    }
    .detail {
        padding-top: 10px;
        padding-bottom: 10px;
        position: relative;
    }
    .details-form {
        text-align: left;
    }
    .details-form:focus {
        border-color: #00B4CC;
    }
    .textarea {
        padding: 10px;
        display: block;
        width: 90vw;
        overflow: hidden;
        resize: vertical;
        min-height: 40px;
        line-height: 20px;
        border: solid;
        border-width: 1px;
        border-color: #CED4DA;
        font-size: 1em;
        font-family: 'Comfortaa', Arial;
        color: #000000;
    }
    .textarea-list {
        margin-bottom: 10px;
        height: 40px;
    }
    .btn-group, .btn {
        width: 100%;
    }
    .btn-outline-secondary {
        border-color: #CED4DA;
    }
    .btn.btn-outline-secondary:active:hover,
    .btn.btn-outline-secondary:hover,
    .btn-outline-secondary:not(:disabled):not(.disabled).active {
        background-color: #00B4CC;
    }
    .description::placeholder {
        color: #CED4DA;
    }
    .story::placeholder {
        color: #CED4DA;
    }
    .component-names::placeholder {
        color: #CED4DA;
    }
    .method-count {
        padding-top: 8px;
        padding-left: 10px;
        position: absolute;
        color: #00B4CC;
        font-weight: 400;
    }
    .method {
        padding-left: 25px;
    }
    .ingredients {
        padding-top: 15px;
    }
    .ingredient-heading {
        font-size: 1em;
        padding: 10px;
        margin-bottom: 10px;
        color: #ffffff;
        background-color: #00B4CC;
        height: 40px;
    }
    .tags {
        height: 40px;
    }
    .tags::placeholder {
        color: #CED4DA;
    }
    .textarea:focus {
        border-color: #00B4CC;
        box-shadow: none;
        outline: none !important;
    }

    /* Transitions */
    .fade-enter-active, .fade-leave-active {
        transition: opacity 500ms ease-in-out;
    }
    .fade-enter-to, .fade-leave {
        opacity: 1;
    }
    .fade-enter, .fade-leave-to {
        opacity: 0;
    }
    .fade-delay-enter-active {
        transition: opacity 500ms ease-in-out;
        transition-delay: 1s;
    }
    .fade-delay-enter-to {
        opacity: 1;
    }
    .fade-delay-enter {
        opacity: 0;
    }
    .list-item {
        transition: all 200ms;
        display: inline-block;
        margin-right: 10px;
    }
    .list-leave-active {
        position: absolute;
    }
    .list-enter-active {
        transition: transform 200ms, opacity 200ms;
    }
    .list-enter, .list-leave-to /* .list-leave-active below version 2.1.8 */ {
        opacity: 0;
        transform: translateY(-20px);
    }
    .list-move {
        transition: transform 500ms;
    }
    
    /* Loading animation */
    .loading {
        position: absolute;
        top: 40vh;
        left: 50vw;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }
    .lds-ring {
        left: -32px;
        display: inline-block;
        position: relative;
        width: 64px;
        height: 64px;
    }
    .lds-ring div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: 51px;
        height: 51px;
        margin: 6px;
        border: 6px solid #00B4CC;
        border-radius: 50%;
        animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: #00B4CC transparent transparent transparent;
    }
    .lds-ring div:nth-child(1) {
        animation-delay: -0.45s;
    }
    .lds-ring div:nth-child(2) {
        animation-delay: -0.3s;
    }
    .lds-ring div:nth-child(3) {
        animation-delay: -0.15s;
    }
    @keyframes lds-ring {
        0% {
        transform: rotate(0deg);
        }
        100% {
        transform: rotate(360deg);
        }
    }
</style>